<script lang="ts">
  import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-svelte';
  import { Button } from '$lib/components/ui/button';

  interface Props {
    page: number; // 1-based, current page
    pages: number; // total pages
    total: number; // total items
    pageSize: number;
    onPageChange?: (page: number) => void;
    /** Si esta en true muestra los botones first/last (« y »). */
    showEdges?: boolean;
    /** Loading externo: deshabilita los botones para evitar dobles clicks. */
    disabled?: boolean;
  }

  let {
    page,
    pages,
    total,
    pageSize,
    onPageChange,
    showEdges = true,
    disabled = false,
  }: Props = $props();

  // Calcula que numeros de pagina mostrar. Estilo "1 2 3 ... 10 11 12".
  // Reglas:
  //   - siempre mostramos pagina 1, pagina actual, pagina ultima.
  //   - alrededor de la actual mostramos +/- 1.
  //   - el resto se reemplaza por '...' (sentinel).
  let visiblePages = $derived.by((): Array<number | 'ellipsis-l' | 'ellipsis-r'> => {
    if (pages <= 7) {
      return Array.from({ length: pages }, (_, i) => i + 1);
    }
    const out: Array<number | 'ellipsis-l' | 'ellipsis-r'> = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(pages - 1, page + 1);
    if (start > 2) out.push('ellipsis-l');
    for (let p = start; p <= end; p++) out.push(p);
    if (end < pages - 1) out.push('ellipsis-r');
    out.push(pages);
    return out;
  });

  let firstItem = $derived(total === 0 ? 0 : (page - 1) * pageSize + 1);
  let lastItem = $derived(Math.min(page * pageSize, total));

  function go(p: number): void {
    if (p < 1 || p > pages || p === page) return;
    onPageChange?.(p);
  }
</script>

{#if pages > 0}
  <div class="flex flex-col items-center justify-between gap-3 sm:flex-row">
    <p class="text-muted-foreground text-xs tabular-nums">
      Showing <span class="text-foreground font-medium">{firstItem}-{lastItem}</span>
      of <span class="text-foreground font-medium">{total}</span>
    </p>

    <div class="flex items-center gap-1">
      {#if showEdges}
        <Button
          variant="ghost"
          size="icon"
          class="size-8"
          {disabled}
          aria-label="First page"
          onclick={() => go(1)}
        >
          <ChevronsLeft class="size-4" />
        </Button>
      {/if}
      <Button
        variant="ghost"
        size="icon"
        class="size-8"
        disabled={disabled || page <= 1}
        aria-label="Previous page"
        onclick={() => go(page - 1)}
      >
        <ChevronLeft class="size-4" />
      </Button>

      <!-- Numeros de pagina (compactados con ellipsis) -->
      <div class="hidden items-center gap-0.5 sm:flex">
        {#each visiblePages as item (item)}
          {#if item === 'ellipsis-l' || item === 'ellipsis-r'}
            <span class="text-muted-foreground px-1.5 text-xs">…</span>
          {:else}
            {@const isActive = item === page}
            <Button
              variant={isActive ? 'default' : 'ghost'}
              size="icon"
              class="size-8 text-xs tabular-nums"
              disabled={disabled || isActive}
              aria-label={`Page ${item}`}
              aria-current={isActive ? 'page' : undefined}
              onclick={() => go(item as number)}
            >
              {item}
            </Button>
          {/if}
        {/each}
      </div>

      <!-- Mobile compact: solo "X of Y" -->
      <span class="text-muted-foreground px-2 text-xs tabular-nums sm:hidden">
        {page} / {pages}
      </span>

      <Button
        variant="ghost"
        size="icon"
        class="size-8"
        disabled={disabled || page >= pages}
        aria-label="Next page"
        onclick={() => go(page + 1)}
      >
        <ChevronRight class="size-4" />
      </Button>
      {#if showEdges}
        <Button
          variant="ghost"
          size="icon"
          class="size-8"
          {disabled}
          aria-label="Last page"
          onclick={() => go(pages)}
        >
          <ChevronsRight class="size-4" />
        </Button>
      {/if}
    </div>
  </div>
{/if}
